import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { ImageWithFallback } from "../..";
import { getMatchStatusName, randomAvatar } from "../../../utils/constants";

const MatchHeader = ({
  match,
  playerA,
  playerB,
  division,
  playerARegularDivision,
  playerBRegularDivision,
  matchResultA,
  matchResultB,
  matchResultAWA,
  matchResultAWB,
  matchStatus,
}) => {
  const isPlayoff = match.phase === "PLAYOFF";

  return (
    <Card>
      <StatusRow>
        <StatusPill $status={(matchStatus || "").toLowerCase()}>
          {getMatchStatusName(matchStatus)}
        </StatusPill>
      </StatusRow>

      {isPlayoff ? (
        <PhaseLabel>Playoff {match.playoffType}</PhaseLabel>
      ) : null}
      {match.phase === "REGULAR_LEAGUE" ? (
        <PhaseLabel>Jornada {match.leagueDay}</PhaseLabel>
      ) : null}

      <VSMain>
        <PlayerMini>
          <PlayerMiniAvatar>
            <ImageWithFallback
              src={playerA?.discordAvatarURL || randomAvatar()}
              alt={playerA?.username}
              fallbackSrc={randomAvatar()}
            />
          </PlayerMiniAvatar>
          <PlayerMiniName>{playerA?.username}</PlayerMiniName>
        </PlayerMini>
        <VSBox>
          <VSResult>
            <AWTag>{matchResultAWA}</AWTag>
            {matchResultA} : {matchResultB}
            <AWTag>{matchResultAWB}</AWTag>
          </VSResult>
        </VSBox>
        <PlayerMini>
          <PlayerMiniAvatar>
            <ImageWithFallback
              src={playerB?.discordAvatarURL || randomAvatar()}
              alt={playerB?.username}
              fallbackSrc={randomAvatar()}
            />
          </PlayerMiniAvatar>
          <PlayerMiniName>{playerB?.username}</PlayerMiniName>
        </PlayerMini>
      </VSMain>

      {isPlayoff ? (
        <PlayoffDivisionsRow>
          {playerARegularDivision ? (
            <PlayerDivisionBadge
              to={`/division/${playerARegularDivision.divisionId}/group/${playerARegularDivision.groupId}`}
            >
              <PlayerDivisionLabel>{playerA?.username}</PlayerDivisionLabel>
              <DivisionName>
                {playerARegularDivision.divisionName}
                {playerARegularDivision.groupName
                  ? ` — ${playerARegularDivision.groupName}`
                  : ""}
              </DivisionName>
            </PlayerDivisionBadge>
          ) : null}
          {playerBRegularDivision ? (
            <PlayerDivisionBadge
              to={`/division/${playerBRegularDivision.divisionId}/group/${playerBRegularDivision.groupId}`}
            >
              <PlayerDivisionLabel>{playerB?.username}</PlayerDivisionLabel>
              <DivisionName>
                {playerBRegularDivision.divisionName}
                {playerBRegularDivision.groupName
                  ? ` — ${playerBRegularDivision.groupName}`
                  : ""}
              </DivisionName>
            </PlayerDivisionBadge>
          ) : null}
        </PlayoffDivisionsRow>
      ) : division ? (
        <DivisionBadge
          to={`/division/${match.divisionId}/group/${match.groupId}`}
        >
          <DivisionImg src={division.image} alt={division.name} />
          <DivisionName>
            {division.name} — {match.groupName}
          </DivisionName>
        </DivisionBadge>
      ) : null}
    </Card>
  );
};

const Card = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: center;
`;

const StatusPill = styled.span`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.3rem 0.85rem;
  border-radius: 24px;
  border: 1px solid;

  background: ${({ $status }) =>
    $status === "finished"
      ? "rgba(60,255,208,0.12)"
      : $status === "reviewed"
      ? "rgba(60,255,208,0.12)"
      : $status === "not_started" || $status === "not-started"
      ? "rgba(201,160,78,0.12)"
      : $status === "pending_date"
      ? "rgba(255,59,59,0.12)"
      : "rgba(255,255,255,0.04)"};

  color: ${({ $status }) =>
    $status === "finished" || $status === "reviewed"
      ? "var(--v-mint)"
      : $status === "not_started" || $status === "not-started"
      ? "var(--v-gold)"
      : $status === "pending_date"
      ? "#ff3b3b"
      : "var(--v-fg-muted)"};

  border-color: ${({ $status }) =>
    $status === "finished" || $status === "reviewed"
      ? "var(--v-mint)"
      : $status === "not_started" || $status === "not-started"
      ? "var(--v-gold)"
      : $status === "pending_date"
      ? "#ff3b3b"
      : "rgba(255,255,255,0.08)"};
`;

const PhaseLabel = styled.h2`
  font-family: var(--v-font-display);
  font-size: 60px;
  font-weight: 400;
  color: var(--v-gold);
  text-align: center;
  margin: 0;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 1px;
  word-break: break-word;
`;

const VSMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const PlayerMini = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  flex: 1;
  min-width: 0;
`;

const PlayerMiniAvatar = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlayerMiniName = styled.span`
  font-family: var(--v-font-body);
  font-size: 13px;
  font-weight: 600;
  color: var(--v-fg);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const VSBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const VSResult = styled.div`
  font-family: var(--v-font-mono);
  font-size: 28px;
  font-weight: 700;
  color: var(--v-fg);
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const AWTag = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--v-gold);
`;

const DivisionBadge = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  padding: 0.4rem 0.75rem;
  border: 1px solid rgba(201, 160, 78, 0.2);
  border-radius: 24px;
`;

const PlayoffDivisionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-between;
`;

const PlayerDivisionBadge = styled(Link)`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  text-decoration: none;
  padding: 0.4rem 0.75rem;
  border: 1px solid rgba(201, 160, 78, 0.2);
  border-radius: 24px;
`;

const PlayerDivisionLabel = styled.span`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const DivisionImg = styled.img`
  width: 1.5rem;
  height: 1.5rem;
  object-fit: contain;
`;

const DivisionName = styled.span`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-gold);
`;

export default MatchHeader;
